import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../../api";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";
import PlanAddons from "../../../components/plan-addons";
import { Tag, Plus, Trash2, RefreshCw } from "lucide-react";

export default function Sale() {
  const {
    getSettingsPromoCode,
    updateSettingsPromoCode,
    deleteSettingsPromoCode,
  } = useContext(APIContext);

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("promo"); // "promo" | "addons"

  const [sale_promo_code_saved, setSale_promo_code_saved] = useState("");
  const [sale_promo_code, setSale_promo_code] = useState("");

  const [isPromoUpdating, setIsPromoUpdating] = useState(false);
  const [isPromoDeleting, setIsPromoDeleting] = useState(false);

  useEffect(() => {
    getSettingsPromoCode()
      .then((res) => {
        setSale_promo_code(res?.promo_code || "");
        setSale_promo_code_saved(res?.promo_code || "");
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  }, [getSettingsPromoCode]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <PulseLoader size={10} color="#623ce7" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Sales Management</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Configure system default promotional discount code and plan addons.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setActiveTab("promo")}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeTab === "promo"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Promo Code
          </button>
          <button
            onClick={() => setActiveTab("addons")}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeTab === "addons"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Plan Addons
          </button>
        </div>
      </div>

      {activeTab === "promo" && (
        <div className="max-w-md pt-2 space-y-4">
          <div>
            <label
              htmlFor="promo_code"
              className="block text-sm font-semibold text-slate-700 mb-1.5"
            >
              Default Sale Promo Code
            </label>
            <div className="relative">
              <input
                id="promo_code"
                type="text"
                placeholder="e.g. SUMMER50"
                value={sale_promo_code}
                onChange={(e) => setSale_promo_code(e.target.value)}
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm uppercase tracking-wide focus:outline-none focus:border-[#623ce7] focus:ring-1 focus:ring-[#623ce7] transition-colors"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              {sale_promo_code_saved ? (
                <>
                  Current active promo:{" "}
                  <strong className="font-mono text-emerald-600">
                    {sale_promo_code_saved}
                  </strong>
                </>
              ) : (
                "No default promo code currently configured."
              )}
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => {
                if (isPromoUpdating) return;
                setIsPromoUpdating(true);

                updateSettingsPromoCode(sale_promo_code)
                  .then(() => {
                    setIsPromoUpdating(false);
                    toast.success("Promo code updated successfully!");
                    setSale_promo_code_saved(sale_promo_code);
                  })
                  .catch((error) => {
                    console.error(error);
                    setIsPromoUpdating(false);
                    toast.error("Failed to update promo code");
                  });
              }}
              disabled={isPromoUpdating || !sale_promo_code.trim()}
              className="bg-[#623ce7] hover:bg-[#5030bc] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 shadow-sm shadow-[#623ce7]/20"
            >
              {isPromoUpdating ? (
                <PulseLoader size={6} color="#ffffff" />
              ) : !sale_promo_code_saved ? (
                "Create Promo"
              ) : (
                "Update Promo"
              )}
            </button>

            {sale_promo_code_saved && (
              <button
                onClick={() => {
                  if (isPromoDeleting) return;
                  setIsPromoDeleting(true);
                  deleteSettingsPromoCode()
                    .then(() => {
                      setIsPromoDeleting(false);
                      setSale_promo_code("");
                      setSale_promo_code_saved("");
                      toast.success("Promo code deleted!");
                    })
                    .catch((err) => {
                      console.error(err);
                      setIsPromoDeleting(false);
                      toast.error("Error deleting promo code");
                    });
                }}
                disabled={isPromoDeleting}
                className="px-3.5 py-2 text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5"
              >
                {isPromoDeleting ? (
                  <PulseLoader size={6} color="#e11d48" />
                ) : (
                  <>
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {activeTab === "addons" && (
        <div className="pt-2">
          <PlanAddons />
        </div>
      )}
    </div>
  );
}